function safeTrim(value) {
  return value == null ? "" : String(value).trim();
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const LINKEDIN_SUSPICIOUS_REPEAT_SUFFIX_REGEX = /(?:^|[-_])([a-z0-9]{1,2})\1{2,}$/i;
const LINKEDIN_DISALLOWED_PATH_PREFIXES = [
  "/404",
  "/authwall",
  "/checkpoint",
  "/events/",
  "/feed/",
  "/groups/",
  "/help/",
  "/jobs/",
  "/learning/",
  "/posts/",
  "/pulse/",
  "/school/",
  "/search/",
  "/showcase/",
];
const SOCIAL_HOSTS = {
  linkedin: ["linkedin.com", "lnkd.in"],
  facebook: ["facebook.com", "fb.com"],
  twitter: ["twitter.com", "x.com"],
  x: ["twitter.com", "x.com"],
  google: ["google.com", "g.page"],
  instagram: ["instagram.com"],
  youtube: ["youtube.com", "youtu.be"],
  tiktok: ["tiktok.com"],
};

function normalizeHttpUrl(value = "", { allowRelative = false } = {}) {
  const raw = safeTrim(value);
  if (!raw) return "";

  if (allowRelative && (/^\/(?!\/)/.test(raw) || raw.startsWith("./") || raw.startsWith("../"))) {
    return raw;
  }

  const candidate = /^https?:\/\//i.test(raw)
    ? raw
    : `https://${raw.replace(/^\/+/, "")}`;

  try {
    const parsed = new URL(candidate);
    if (!["http:", "https:"].includes(parsed.protocol)) return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function hostnameMatches(hostname = "", allowedHosts = []) {
  const normalizedHost = hostname.replace(/^www\./i, "").toLowerCase();
  return allowedHosts.some(
    (host) => normalizedHost === host || normalizedHost.endsWith(`.${host}`)
  );
}

function normalizeLinkedInSlugSegment(value = "") {
  return safeTrim(value)
    .toLowerCase()
    .replace(/[^a-z0-9-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function buildCanonicalLinkedInProfilePath(pathname = "") {
  const normalizedPath = `/${safeTrim(pathname).replace(/^\/+/, "")}`.replace(/\/+/g, "/");
  const lowerPath = normalizedPath.toLowerCase();
  if (!lowerPath || lowerPath === "/") return "";
  if (LINKEDIN_DISALLOWED_PATH_PREFIXES.some((prefix) => lowerPath.startsWith(prefix))) {
    return "";
  }

  const resolvedPath = lowerPath.startsWith("/mwlite/in/")
    ? normalizedPath.replace(/^\/mwlite/i, "")
    : normalizedPath;
  const resolvedLowerPath = resolvedPath.toLowerCase();
  const segments = resolvedPath.split("/").filter(Boolean);
  if (!segments.length) return "";

  if (resolvedLowerPath.startsWith("/company/")) {
    const slug = segments[1] || "";
    if (!slug) return "";
    return `/company/${slug}/`;
  }

  if (resolvedLowerPath.startsWith("/in/")) {
    const slug = normalizeLinkedInSlugSegment(segments[1] || "");
    if (!slug || LINKEDIN_SUSPICIOUS_REPEAT_SUFFIX_REGEX.test(slug)) return "";
    return `/in/${slug}/`;
  }

  if (resolvedLowerPath.startsWith("/pub/")) {
    const normalizedSegments = segments
      .slice(0, 5)
      .map((segment, index) =>
        index === 0 ? segment.toLowerCase() : normalizeLinkedInSlugSegment(segment)
      )
      .filter(Boolean);
    const slug = normalizedSegments.slice(1).join("-");
    if (!normalizedSegments.length || normalizedSegments.length < 2) return "";
    if (!slug || LINKEDIN_SUSPICIOUS_REPEAT_SUFFIX_REGEX.test(slug)) return "";
    return `/${normalizedSegments.join("/")}/`;
  }

  return "";
}

function normalizeLinkedInContactUrl(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";

  try {
    const candidate = /^https?:\/\//i.test(raw)
      ? raw
      : `https://${raw.replace(/^\/+/, "")}`;
    const parsed = new URL(candidate);
    if (!["http:", "https:"].includes(parsed.protocol)) return "";

    const normalizedHost = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    if (normalizedHost === "lnkd.in" || normalizedHost.endsWith(".lnkd.in")) {
      return parsed.toString();
    }
    if (!(normalizedHost === "linkedin.com" || normalizedHost.endsWith(".linkedin.com"))) {
      return "";
    }

    const canonicalPath = buildCanonicalLinkedInProfilePath(parsed.pathname || "");
    if (!canonicalPath) return "";

    return `https://www.linkedin.com${canonicalPath}`;
  } catch {
    return "";
  }
}

export function normalizeContactEmail(value = "") {
  const raw = safeTrim(value).replace(/^mailto:\s*/i, "");
  if (!raw || !EMAIL_PATTERN.test(raw)) return "";
  return raw;
}

export function normalizeContactPhone(value = "") {
  const raw = safeTrim(value).replace(/^tel:\s*/i, "");
  if (!raw || raw.includes("@")) return "";
  const digitCount = raw.replace(/\D/g, "").length;
  return digitCount >= 5 ? raw : "";
}

export function normalizeContactSocialUrl(type = "website", value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";

  if (type === "linkedin") {
    const candidate = /^\/?(?:in|pub|mwlite\/in)\//i.test(raw)
      ? `https://www.linkedin.com/${raw.replace(/^\/+/, "")}`
      : raw;
    // Validate profile links without rewriting them. LinkedIn URLs can carry
    // meaningful casing, query parameters, and tracking/identity segments
    // returned by the contact-discovery agent.
    return normalizeLinkedInContactUrl(candidate) ? candidate : "";
  }

  let candidate = raw;
  const normalized = normalizeHttpUrl(candidate);
  if (!normalized) return "";

  if (!(type in SOCIAL_HOSTS)) return normalized;

  try {
    const parsed = new URL(normalized);
    return hostnameMatches(parsed.hostname, SOCIAL_HOSTS[type]) ? parsed.toString() : "";
  } catch {
    return "";
  }
}

export function normalizeContactImageUrl(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";
  if (/^data:image\//i.test(raw)) return raw;
  return normalizeHttpUrl(raw, { allowRelative: true });
}

export function normalizeContactActionHref(type = "website", value = "") {
  switch (type) {
    case "email":
      return normalizeContactEmail(value);
    case "phone":
      return normalizeContactPhone(value);
    case "linkedin":
    case "facebook":
    case "twitter":
    case "x":
    case "google":
    case "instagram":
    case "youtube":
    case "tiktok":
      return normalizeContactSocialUrl(type, value);
    case "website":
    case "resume":
      return normalizeHttpUrl(value);
    case "sms":
      return normalizeContactPhone(value);
    default:
      return safeTrim(value);
  }
}
